import React, { useEffect, useState, useCallback } from 'react';

const API_BASE = '/api/training';

const KURSER = [
  {
    id: 'foraeldreansvarsloven',
    titel: 'Forældreansvarsloven § for §',
    ikon: '📚',
    kilde: 'LBK nr. 662 af 1. juli 2026',
    meta: '28 kapitler · 72 scener · 28 spørgsmål',
    farve: '#3b82f6',
  },
  {
    id: 'barnetslov',
    titel: 'Barnets lov § for §',
    ikon: '⚖️',
    kilde: 'LBK nr. 642 af 1. juli 2026',
    meta: '25 kapitler · §§ 1-214 · 51 quiz-scener',
    farve: '#8b5cf6',
  },
];

const FILTRE = [
  { id: 'alle',          label: 'Alle kurser' },
  { id: 'igangværende',  label: 'Igangværende' },
  { id: 'gennemfoert',   label: 'Gennemført' },
  { id: 'ikke-startet',  label: 'Ikke startet' },
];

function statusFraProgress(p) {
  if (!p) return 'ikke-startet';
  if (p.completedAt) return 'gennemfoert';
  if (p.seen && p.seen.length > 0) return 'igangværende';
  return 'ikke-startet';
}

const BADGE = {
  'gennemfoert':  { tekst: 'Gennemført',    bg: 'rgba(16,185,129,0.15)', txt: '#34d399', border: 'rgba(16,185,129,0.3)' },
  'igangværende': { tekst: 'Igangværende',  bg: 'rgba(245,158,11,0.15)', txt: '#fbbf24', border: 'rgba(245,158,11,0.3)' },
  'ikke-startet': { tekst: 'Ikke startet',  bg: 'rgba(100,116,139,0.15)', txt: '#94a3b8', border: 'rgba(100,116,139,0.3)' },
};

export default function KursusHub({ bruger, onValgKursus }) {
  const [progressMap, setProgressMap] = useState({});
  const [filter, setFilter] = useState('alle');
  const [loading, setLoading] = useState(true);

  const hentProgress = useCallback(async () => {
    setLoading(true);
    const resultater = await Promise.all(
      KURSER.map(async (k) => {
        try {
          const r = await fetch(`${API_BASE}/${k.id}/progress`, { credentials: 'include' });
          if (!r.ok) return [k.id, null];
          return [k.id, await r.json()];
        } catch {
          return [k.id, null];
        }
      })
    );
    setProgressMap(Object.fromEntries(resultater));
    setLoading(false);
  }, []);

  useEffect(() => { hentProgress(); }, [hentProgress]);

  const visKurser = KURSER.filter(k => {
    if (filter === 'alle') return true;
    return statusFraProgress(progressMap[k.id]) === filter;
  });

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 16px' }}>

      {/* Overskrift */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontSize: 26, fontWeight: 700, color: '#f1f5f9', marginBottom: 6 }}>
          🎓 Kurser
        </div>
        <div style={{ fontSize: 14, color: '#64748b' }}>
          Lovpligtige og faglige e-læringsmoduler — genoptag når som helst
        </div>
      </div>

      {/* Filtre */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 28, flexWrap: 'wrap' }}>
        {FILTRE.map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            style={{
              padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.15s',
              border: filter === f.id ? 'none' : '1px solid rgba(255,255,255,0.08)',
              background: filter === f.id ? '#3fd4c7' : 'rgba(255,255,255,0.04)',
              color: filter === f.id ? '#0a0e2e' : '#94a3b8',
            }}
          >{f.label}</button>
        ))}
      </div>

      {/* Kursus-kort */}
      {loading ? (
        <div style={{ color: '#64748b', fontSize: 14, padding: '32px 0' }}>Henter kursusoversigt…</div>
      ) : visKurser.length === 0 ? (
        <div style={{ color: '#64748b', fontSize: 14, padding: '32px 0' }}>
          Ingen kurser matcher filteret.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {visKurser.map(k => {
            const status = statusFraProgress(progressMap[k.id]);
            const badge = BADGE[status];
            const p = progressMap[k.id];
            const seenCount = p?.seen?.length ?? 0;

            return (
              <div
                key={k.id}
                role="button"
                tabIndex={0}
                onClick={() => onValgKursus(k.id)}
                onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onValgKursus(k.id)}
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: 14,
                  padding: '24px 22px',
                  cursor: 'pointer',
                  transition: 'all 0.18s',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.055)';
                  e.currentTarget.style.borderColor = k.farve + '55';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {/* Farvestribe i toppen */}
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: k.farve, opacity: 0.6, borderRadius: '14px 14px 0 0' }} />

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
                  <div style={{ fontSize: 32, lineHeight: 1 }}>{k.ikon}</div>
                  <span style={{
                    padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                    background: badge.bg, color: badge.txt, border: `1px solid ${badge.border}`,
                  }}>{badge.tekst}</span>
                </div>

                {/* Titel */}
                <div style={{ fontSize: 17, fontWeight: 700, color: '#f1f5f9', marginBottom: 6, lineHeight: 1.3 }}>
                  {k.titel}
                </div>

                {/* Kilde */}
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 14 }}>{k.kilde}</div>

                {/* Meta */}
                <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>{k.meta}</div>

                {/* Fremgang */}
                {status === 'igangværende' && (
                  <div style={{ fontSize: 12, color: '#fbbf24' }}>
                    {seenCount} scene{seenCount !== 1 ? 'r' : ''} set
                  </div>
                )}
                {status === 'gennemfoert' && p?.completedAt && (
                  <div style={{ fontSize: 12, color: '#34d399' }}>
                    Afsluttet {new Date(p.completedAt).toLocaleDateString('da-DK', { day:'numeric', month:'long', year:'numeric' })}
                  </div>
                )}

                {/* CTA-pil */}
                <div style={{ position: 'absolute', bottom: 22, right: 20, fontSize: 18, color: '#475569' }}>→</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
